import { z } from 'zod';
import type {
  CreatableCustomFieldType,
  CustomFieldAssociation,
  CustomFieldDefinition,
  CustomFieldEntityType,
  CustomFieldInheritanceRule,
  CustomFieldType,
  InheritanceBehavior,
  SnapshotPolicy,
} from '@/types/custom-fields';

export type FieldColumn =
  | 'field'
  | 'type'
  | 'entities'
  | 'required'
  | 'customerFacing'
  | 'updatedAt';

export type FieldTypeFilter = 'all' | CustomFieldType;
export type FieldEntityFilter = 'all' | 'unassociated' | CustomFieldEntityType;
export type FieldStatusFilter = 'all' | 'active' | 'inactive';

export type DeleteTarget =
  | { type: 'definition'; id: string; label: string }
  | { type: 'association'; id: string; label: string }
  | { type: 'rule'; id: string; label: string };

export interface FieldMeta {
  definition: CustomFieldDefinition;
  associations: CustomFieldAssociation[];
  inheritanceRules: CustomFieldInheritanceRule[];
}

export type AssociationVisibility = 'off' | 'form' | 'create';
export type UsageVisibilityDraft = 'OFF' | 'FORM' | 'FORM_CREATE';

export interface EntityUsageDraft {
  entityType: CustomFieldEntityType;
  associationId?: string;
  visibility: UsageVisibilityDraft;
  isRequired: boolean;
  isCustomerFacing: boolean;
  snapshotPolicy: SnapshotPolicy;
  /**
   * NOT-369 — JOB usage only. Job Type ids this field is scoped to; an empty
   * list means the field applies to every job. Ignored for other entities.
   */
  jobTypeIds: string[];
  source: {
    sourceEntityType: CustomFieldEntityType;
    behavior: InheritanceBehavior;
  } | null;
}

/**
 * Creatable field types (NOT-193): 6 options. CHECKBOX is excluded — it's
 * deprecated because false-by-default with no third state is indistinguishable
 * from "unanswered" on tri-state questions ("Inspection passed?"). Use a
 * DROPDOWN with ["Yes","No"] (or ["Yes","No","N/A"]) instead.
 */
export const FIELD_TYPE_OPTIONS: { value: CreatableCustomFieldType; label: string }[] = [
  { value: 'TEXT', label: 'Text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'DROPDOWN', label: 'Dropdown' },
  { value: 'MULTI_SELECT', label: 'Multi-select' },
  { value: 'DATE', label: 'Date' },
  { value: 'CURRENCY', label: 'Currency' },
];

export const FIELD_TYPE_FILTER_OPTIONS: { value: FieldTypeFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  ...FIELD_TYPE_OPTIONS,
];

/**
 * Display labels for all field types, INCLUDING the deprecated CHECKBOX,
 * so legacy rows still render with a human-readable type label.
 */
export const FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  TEXT: 'Text',
  NUMBER: 'Number',
  DROPDOWN: 'Dropdown',
  MULTI_SELECT: 'Multi-select',
  DATE: 'Date',
  CHECKBOX: 'Checkbox',
  CURRENCY: 'Currency',
};

export const ENTITY_TYPE_OPTIONS: { value: CustomFieldEntityType; label: string }[] = [
  { value: 'contact', label: 'Contact' },
  { value: 'address', label: 'Address' },
  { value: 'estimate', label: 'Estimate' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'job', label: 'Job' },
  { value: 'equipment_description', label: 'Equipment description' },
];

export const ENTITY_FILTER_OPTIONS: { value: FieldEntityFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  ...ENTITY_TYPE_OPTIONS,
  { value: 'unassociated', label: 'Unassociated' },
];

export const ENTITY_TYPE_LABELS: Record<CustomFieldEntityType, string> = {
  contact: 'Contact',
  address: 'Address',
  estimate: 'Estimate',
  invoice: 'Invoice',
  job: 'Job',
  equipment_description: 'Equipment description',
};

export const ENTITY_TYPE_PLURAL_LABELS: Record<CustomFieldEntityType, string> = {
  contact: 'Contacts',
  address: 'Addresses',
  estimate: 'Estimates',
  invoice: 'Invoices',
  job: 'Jobs',
  equipment_description: 'Equipment descriptions',
};

/**
 * The snapshot policy each snapshot-capable entity actually triggers: an estimate
 * freezes its custom fields on SEND, an invoice on FINALIZE. The editor exposes a
 * single "freeze when sent" toggle that maps to the right policy per entity, so the
 * user never picks a policy the entity won't honour (the old ON_SEND/ON_FINALIZE
 * mismatch). Only `estimate` and `invoice` snapshot — see CUSTOMER_FACING_ENTITY_TYPES.
 */
export function snapshotPolicyForEntity(entityType: CustomFieldEntityType): SnapshotPolicy {
  return entityType === 'estimate' ? 'ON_SEND' : 'ON_FINALIZE';
}

/**
 * Plain-language name for a STORED inheritance behavior.
 *
 * `ALWAYS_SYNC` keeps its own name — a legacy row genuinely holds that value and
 * calling it "Copy once" would misreport what is stored — but it is marked as
 * unsupported, because nothing has kept such a value in sync since the behavior
 * was deprecated on 2026-06-11 (73932340e). There is deliberately no
 * `BEHAVIOR_OPTIONS` counterpart: `COPY_ON_CREATE` is the only behavior the API
 * accepts, so there is nothing to choose between and nothing to offer.
 */
export const BEHAVIOR_LABELS: Record<InheritanceBehavior, string> = {
  COPY_ON_CREATE: 'Copy once',
  ALWAYS_SYNC: 'Keep in sync (no longer supported)',
};

export const STATUS_OPTIONS: { value: FieldStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

export const ALLOWED_INHERITANCE_PATHS: Record<CustomFieldEntityType, CustomFieldEntityType[]> = {
  contact: ['estimate', 'invoice', 'job'],
  address: ['estimate', 'invoice', 'job'],
  estimate: [],
  invoice: [],
  job: ['invoice'],
  // Equipment description is a leaf: no custom-field inheritance in or out.
  equipment_description: [],
};

/**
 * Source → target → allowed behaviors. Mirrors the BE's `ALLOWED_INHERITANCE_PATHS`
 * (target → source → behaviors) but inverted to match how the FE builds the picker:
 * the user first picks a source, then a target, then a behavior.
 *
 * Every pair now allows `COPY_ON_CREATE` and nothing else, so this map records
 * which source→target hops exist at all rather than which behaviors they permit.
 */
export const ALLOWED_BEHAVIORS: Record<
  CustomFieldEntityType,
  Partial<Record<CustomFieldEntityType, InheritanceBehavior[]>>
> = {
  contact: {
    estimate: ['COPY_ON_CREATE'],
    invoice: ['COPY_ON_CREATE'],
    job: ['COPY_ON_CREATE'],
  },
  address: {
    estimate: ['COPY_ON_CREATE'],
    invoice: ['COPY_ON_CREATE'],
    // ALWAYS_SYNC deprecated: a job stores its service address as embedded JSON
    // (no Address-row id) so an address→job value can't be resolved. COPY only.
    job: ['COPY_ON_CREATE'],
  },
  estimate: {},
  invoice: {},
  job: {
    invoice: ['COPY_ON_CREATE'],
  },
  // Equipment description is a leaf: no outbound inheritance to any target.
  equipment_description: {},
};

export const CUSTOMER_FACING_ENTITY_TYPES: CustomFieldEntityType[] = ['estimate', 'invoice'];

export const ASSOCIATION_VISIBILITY_LABELS: Record<AssociationVisibility, string> = {
  off: 'Off the form',
  form: 'On the form',
  create: 'Form + create',
};

const optionSchema = z.object({
  label: z.string(),
  key: z.string().optional().or(z.literal('')),
});

const customFieldEntityTypeSchema = z.enum([
  'contact',
  'address',
  'estimate',
  'invoice',
  'job',
  'equipment_description',
]);
const entityUsageDraftSchema = z.object({
  entityType: customFieldEntityTypeSchema,
  associationId: z.string().optional(),
  visibility: z.enum(['OFF', 'FORM', 'FORM_CREATE']),
  isRequired: z.boolean(),
  isCustomerFacing: z.boolean(),
  snapshotPolicy: z.enum(['NONE', 'ON_SEND', 'ON_FINALIZE']),
  jobTypeIds: z.array(z.string()),
  source: z
    .object({
      sourceEntityType: customFieldEntityTypeSchema,
      behavior: z.enum(['COPY_ON_CREATE', 'ALWAYS_SYNC']),
    })
    .nullable(),
});

export const DEFINITION_LABEL_MAX_LENGTH = 255;
export const DEFINITION_DESCRIPTION_MAX_LENGTH = 500;

const definitionSchemaBase = z.object({
  label: z
    .string()
    .min(1, 'Label is required')
    .max(
      DEFINITION_LABEL_MAX_LENGTH,
      `Label must be ${DEFINITION_LABEL_MAX_LENGTH} characters or fewer`,
    ),
  // NOT-193: CHECKBOX is deprecated and never offered by the type-picker, but it
  // remains in the schema so legacy definitions can still be edited (label,
  // description, isActive). Creates with CHECKBOX are blocked by the BE DTO.
  fieldType: z.enum(['TEXT', 'NUMBER', 'DROPDOWN', 'MULTI_SELECT', 'DATE', 'CHECKBOX', 'CURRENCY']),
  fieldKey: z
    .string()
    .regex(/^[a-z0-9_]*$/, 'Use lowercase letters, numbers, and underscores only')
    .optional(),
  description: z
    .string()
    .max(
      DEFINITION_DESCRIPTION_MAX_LENGTH,
      `Description must be ${DEFINITION_DESCRIPTION_MAX_LENGTH} characters or fewer`,
    )
    .optional()
    .or(z.literal('')),
  isActive: z.boolean(),
  maxLength: z.number().int().positive().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  precision: z.number().int().min(0).max(10).optional(),
  options: z.array(optionSchema).optional(),
  maxSelections: z.number().int().positive().optional(),
  usage: z.array(entityUsageDraftSchema),
});

export const definitionSchema = definitionSchemaBase.superRefine((values, ctx) => {
  if (values.fieldType !== 'DROPDOWN' && values.fieldType !== 'MULTI_SELECT') return;

  const options = values.options ?? [];
  if (options.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['options'],
      message: 'Add at least one choice.',
    });
    return;
  }

  let validChoiceCount = 0;
  options.forEach((option, index) => {
    const label = option.label.trim();
    if (!label) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['options', index, 'label'],
        message: 'Choice label required',
      });
      return;
    }

    validChoiceCount += 1;
    const key = generateFieldKey(option.key || label);
    if (!key) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['options'],
        message: 'Use at least one letter or number in each choice.',
      });
    }
  });

  if (validChoiceCount === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['options'],
      message: 'Add at least one choice.',
    });
  }
});

export type DefinitionFormValues = z.infer<typeof definitionSchema>;

export const associationSchema = z.object({
  entityType: z.enum(['contact', 'address', 'estimate', 'invoice', 'job', 'equipment_description']),
  isRequired: z.boolean(),
  isDefault: z.boolean(),
  showOnCreate: z.boolean(),
  displayOrder: z.number().int().min(0),
  isCustomerFacing: z.boolean(),
  snapshotPolicy: z.enum(['NONE', 'ON_SEND', 'ON_FINALIZE']),
});

export type AssociationFormValues = z.infer<typeof associationSchema>;

export const inheritanceSchema = z.object({
  sourceEntityType: z.enum([
    'contact',
    'address',
    'estimate',
    'invoice',
    'job',
    'equipment_description',
  ]),
  targetEntityType: z.enum([
    'contact',
    'address',
    'estimate',
    'invoice',
    'job',
    'equipment_description',
  ]),
  behavior: z.enum(['COPY_ON_CREATE', 'ALWAYS_SYNC']),
  priority: z.number().int().min(0),
});

export type InheritanceFormValues = z.infer<typeof inheritanceSchema>;

export function toNumberOrUndefined(value: string): number | undefined {
  if (value === '') return undefined;
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? undefined : numberValue;
}

export function generateFieldKey(label: string): string {
  return label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function generateUniqueOptionKey(value: string, usedKeys: Set<string>): string {
  const base = generateFieldKey(value);
  if (!base) return '';

  let candidate = base;
  let index = 2;
  while (usedKeys.has(candidate)) {
    candidate = `${base}_${index}`;
    index += 1;
  }
  return candidate;
}

export function formatDate(value: string | null | undefined) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

export function compareValues(a: string | number, b: string | number) {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

export function countRequired(meta: FieldMeta) {
  return meta.associations.filter((association) => association.isRequired).length;
}

export function countCustomerFacing(meta: FieldMeta) {
  return meta.associations.filter((association) => association.isCustomerFacing).length;
}

export function getSortValue(column: FieldColumn, meta: FieldMeta) {
  switch (column) {
    case 'field':
      return meta.definition.label.toLowerCase();
    case 'type':
      return FIELD_TYPE_LABELS[meta.definition.fieldType].toLowerCase();
    case 'entities':
      return meta.associations.length;
    case 'required':
      return countRequired(meta);
    case 'customerFacing':
      return countCustomerFacing(meta);
    case 'updatedAt':
      return Date.parse(meta.definition.updatedAt) || 0;
  }
}

export function emptyMeta(definition: CustomFieldDefinition): FieldMeta {
  return { definition, associations: [], inheritanceRules: [] };
}

export function emptyDefinitionFormValues(): DefinitionFormValues {
  return {
    label: '',
    fieldType: 'TEXT',
    fieldKey: '',
    description: '',
    isActive: true,
    maxLength: undefined,
    min: undefined,
    max: undefined,
    precision: undefined,
    options: [],
    maxSelections: undefined,
    usage: [],
  };
}

export function definitionToFormValues(
  definition: CustomFieldDefinition,
  meta?: FieldMeta | null,
): DefinitionFormValues {
  return {
    label: definition.label,
    fieldType: definition.fieldType,
    fieldKey: definition.fieldKey,
    description: definition.description ?? '',
    isActive: definition.isActive,
    maxLength: definition.config?.maxLength ?? undefined,
    min: definition.config?.min ?? undefined,
    max: definition.config?.max ?? undefined,
    precision: definition.config?.precision ?? undefined,
    options: definition.config?.options ?? [],
    maxSelections: definition.config?.maxSelections ?? undefined,
    usage: meta ? entityUsageDraftsFromMeta(meta) : [],
  };
}

export function emptyAssociationFormValues(): AssociationFormValues {
  return {
    entityType: 'contact',
    isRequired: false,
    isDefault: false,
    showOnCreate: false,
    displayOrder: 0,
    isCustomerFacing: false,
    snapshotPolicy: 'NONE',
  };
}

export function buildDefinitionConfig(values: DefinitionFormValues): Record<string, unknown> {
  const config: Record<string, unknown> = {};
  if (values.fieldType === 'TEXT' && values.maxLength) config.maxLength = values.maxLength;
  if (values.fieldType === 'NUMBER' || values.fieldType === 'CURRENCY') {
    if (values.min != null) config.min = values.min;
    if (values.max != null) config.max = values.max;
    if (values.precision != null) config.precision = values.precision;
  }
  if (values.fieldType === 'DROPDOWN' || values.fieldType === 'MULTI_SELECT') {
    const usedKeys = new Set<string>();
    const options = (values.options ?? []).flatMap((option) => {
      const label = option.label.trim();
      if (!label) return [];

      const key = generateUniqueOptionKey(option.key || label, usedKeys);
      if (!key) return [];

      usedKeys.add(key);
      return [{ label, key }];
    });
    if (options.length > 0) config.options = options;
  }
  if (values.fieldType === 'MULTI_SELECT' && values.maxSelections) {
    config.maxSelections = values.maxSelections;
  }
  return config;
}

export function activeAssociationForDisplay(association: CustomFieldAssociation): boolean {
  return association.isActive !== false;
}

export function visibilityFromAssociation(
  association: Pick<CustomFieldAssociation, 'isRequired' | 'isDefault' | 'showOnCreate'>,
): AssociationVisibility {
  if (association.isRequired && !association.isDefault) return 'form';
  if (association.showOnCreate && association.isDefault) return 'create';
  if (association.isDefault) return 'form';
  return 'off';
}

export function associationPatchFromVisibility(
  visibility: AssociationVisibility,
): Pick<AssociationFormValues, 'isDefault' | 'showOnCreate'> & { isRequired?: boolean } {
  if (visibility === 'off') {
    return { isDefault: false, showOnCreate: false, isRequired: false };
  }
  if (visibility === 'create') {
    return { isDefault: true, showOnCreate: true };
  }
  return { isDefault: true, showOnCreate: false };
}

export function draftVisibilityFromAssociation(
  association: Pick<CustomFieldAssociation, 'isRequired' | 'isDefault' | 'showOnCreate'>,
): UsageVisibilityDraft {
  const visibility = visibilityFromAssociation(association);
  if (visibility === 'create') return 'FORM_CREATE';
  if (visibility === 'form') return 'FORM';
  return 'OFF';
}

export function associationVisibilityFromDraft(
  visibility: UsageVisibilityDraft,
): AssociationVisibility {
  if (visibility === 'FORM_CREATE') return 'create';
  if (visibility === 'FORM') return 'form';
  return 'off';
}

export function associationPatchFromDraft(
  draft: Pick<
    EntityUsageDraft,
    'visibility' | 'isRequired' | 'isCustomerFacing' | 'snapshotPolicy'
  >,
): Pick<
  AssociationFormValues,
  'isRequired' | 'isDefault' | 'showOnCreate' | 'isCustomerFacing' | 'snapshotPolicy'
> {
  const visibility = associationVisibilityFromDraft(draft.visibility);
  return {
    ...associationPatchFromVisibility(visibility),
    isRequired: visibility === 'off' ? false : draft.isRequired,
    isCustomerFacing: draft.isCustomerFacing,
    // Snapshot only applies to customer-facing fields — the BE only captures those.
    snapshotPolicy: draft.isCustomerFacing ? draft.snapshotPolicy : 'NONE',
  };
}

/**
 * NOT-369 — the job-type scope only means anything for JOB usage; any other
 * entity always persists an empty scope ("all instances").
 */
export function jobTypeIdsForSave(draft: EntityUsageDraft): string[] {
  return draft.entityType === 'job' ? draft.jobTypeIds : [];
}

export function jobTypeIdsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, index) => value === sortedB[index]);
}

export function entityUsageDraftsFromMeta(meta: FieldMeta): EntityUsageDraft[] {
  return meta.associations
    .filter(activeAssociationForDisplay)
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((association) => {
      const primaryRule = primaryRuleForTarget(meta.inheritanceRules, association.entityType);
      return {
        entityType: association.entityType,
        associationId: association.id,
        visibility: draftVisibilityFromAssociation(association),
        isRequired: association.isRequired,
        isCustomerFacing: association.isCustomerFacing,
        snapshotPolicy: association.snapshotPolicy,
        jobTypeIds: association.entityType === 'job' ? (association.jobTypeIds ?? []) : [],
        source: primaryRule
          ? {
              sourceEntityType: primaryRule.sourceEntityType,
              behavior: primaryRule.behavior,
            }
          : null,
      };
    });
}

export function allowedSourcesFor(
  targetEntityType: CustomFieldEntityType,
): CustomFieldEntityType[] {
  return ENTITY_TYPE_OPTIONS.map((option) => option.value).filter(
    (sourceEntityType) => ALLOWED_BEHAVIORS[sourceEntityType]?.[targetEntityType]?.length,
  );
}

export function activeRulesForTarget(
  rules: CustomFieldInheritanceRule[],
  targetEntityType: CustomFieldEntityType,
): CustomFieldInheritanceRule[] {
  return rules
    .filter((rule) => rule.isActive !== false && rule.targetEntityType === targetEntityType)
    .sort((a, b) => a.priority - b.priority);
}

export function primaryRuleForTarget(
  rules: CustomFieldInheritanceRule[],
  targetEntityType: CustomFieldEntityType,
): CustomFieldInheritanceRule | null {
  return activeRulesForTarget(rules, targetEntityType)[0] ?? null;
}

export function behaviorAllowedFor(
  sourceEntityType: CustomFieldEntityType,
  targetEntityType: CustomFieldEntityType,
  behavior: InheritanceBehavior,
): boolean {
  return (ALLOWED_BEHAVIORS[sourceEntityType]?.[targetEntityType] ?? []).includes(behavior);
}

export function buildEntityCardSummary(
  association: CustomFieldAssociation,
  rules: CustomFieldInheritanceRule[],
): string {
  const visibility = visibilityFromAssociation(association);
  const parts: string[] = [];

  if (visibility === 'off') {
    parts.push('off the form');
  } else if (visibility === 'create') {
    parts.push(
      `every ${ENTITY_TYPE_LABELS[association.entityType].toLowerCase()}, including create`,
    );
  } else {
    parts.push('when viewing or editing');
  }

  const rule = primaryRuleForTarget(rules, association.entityType);
  if (rule) {
    // True for BOTH behaviors: `resolveInheritedValues` ignores `behavior` and
    // runs only at creation, after which the field is an ordinary editable one.
    // A stored ALWAYS_SYNC rule is named, not renamed, and marked unsupported —
    // saying it "stays in sync" promised something nothing has ever enforced.
    const source = ENTITY_TYPE_LABELS[rule.sourceEntityType].toLowerCase();
    const target = ENTITY_TYPE_LABELS[association.entityType].toLowerCase();
    parts.push(`filled from the ${source} when the ${target} is created`);
    if (rule.behavior === 'ALWAYS_SYNC') {
      parts.push(BEHAVIOR_LABELS.ALWAYS_SYNC.toLowerCase());
    }
  }

  if (visibility !== 'off' && association.isRequired) {
    parts.push('required');
  }

  return parts.join(' · ');
}
